/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Icon } from '@components/atoms'
import { FullModalLayout, Modal } from '@components/molecules'
import {
	BoDetailSections,
	BoDetailSection,
} from '@components/systems/backoffice/BoDetailSections'
import {
	useAppSelector,
	useAppDispatch,
	orderActions,
	routeActions,
	shipmentActions,
	ApiGetDataStatus,
} from '@redux/index'
import { OrderType, OrderDetailType, ShipmentType } from '@schemas'
import {
	mono,
	boFont,
	boShared,
	boStatusChipClass,
	boMedia,
	boTableAsCards,
} from '@components/systems/backoffice/backoffice.tokens'
import { AsYouType, CountryCode, isValidPhoneNumber } from 'libphonenumber-js'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/** 주문 상태 — 그리드 칩과 같은 단어. 색은 `boStatus`(backoffice.tokens) 가 정한다 */
export const ORDER_STATUS_TITLE: { [key: string]: string } = {
	awaitingPayment: '입금 대기',
	new: '신규주문',
	preparing: '배송준비중',
	inTransit: '배송중',
	completed: '완료',
	deleted: '취소',
	refunded: '환불',
}

/**
 * 프리오더 이행 단계 보조 칩 (ORDER_PLAN §3.1). 상태 값은 그대로 두고 배송 준비중 안을 쪼갠다 —
 * 생산 중인 주문이 두 달씩 「배송 준비중」 한 덩어리로 보이지 않게. 흑백 칩(§13.2), 색은 상태 칩만.
 */
const FULFILLMENT_STAGE_TITLE: { [key: string]: string } = {
	part_order: '부품 발주',
	production: '생산중',
	delivery_start: '출고 시작',
	delivery_done: '출고 종료',
}

export const fulfillmentChipTitle = (order: OrderType): string =>
	order.status === 'preparing' && order.hasPreorderLine
		? FULFILLMENT_STAGE_TITLE[order.fulfillmentStage] ?? '생산 전'
		: ''

const SHIPMENT_STATUS_TITLE: { [key: string]: string } = {
	created: '라벨 생성',
	inTransit: '배송중',
	delivered: '배송 완료',
	canceled: '취소',
}
/** 배송 건 상태 → 주문 상태 색. 같은 단계는 같은 색으로 읽힌다 */
const SHIPMENT_STATUS_KEY: { [key: string]: string } = {
	created: 'preparing',
	inTransit: 'inTransit',
	delivered: 'completed',
	canceled: 'deleted',
}

/** ORDER.PAYMENT_METHOD — API `CheckoutPaymentMethod` (paypal · card_usd · card_krw) + 보류값 */
const PAYMENT_METHOD_TITLE: { [key: string]: string } = {
	paypal: 'PayPal',
	paypal_pending: 'PayPal (보류)',
	card_usd: '카드 (USD)',
	card_krw: '카드 (KRW)',
}

const EMPTY = '—'

/** 목록 화면 경로 — 닫기·이전·다음이 모두 URL 로 움직인다 */
const LIST_PATH = '/main/order'
const detailPath = (orderId: string): string =>
	`${LIST_PATH}/${encodeURIComponent(orderId)}`

/**
 * 하단 동작 연결 스위치 — 둘을 따로 둔다.
 *
 * 주문확인은 `ORDER.PREPARED_AT` 한 칸이라 돈도 재고도 움직이지 않는다.
 * 환불은 실제 돈이다. 서버가 게이트웨이를 먼저 읽고 판단하며(ORDER_PLAN §2.2), 되돌릴 수 없다.
 *
 * 취소는 **돈과 무관한 축**이다 — 이 주문을 더 진행하지 않는다는 표시(§2.4). 저장은 소프트 삭제
 * (`DELETED_AT`)를 그대로 쓰고 화면 용어만 '취소'다. 환불이 필요하면 환불을 따로 한다.
 */
const PREPARE_ENABLED = true as boolean
const REFUND_ENABLED = true as boolean
const CANCEL_ENABLED = true as boolean

/** 주문 통화로 금액 — 그리드 열과 같은 규칙. KRW 는 소수 없음, USD 는 둘째 자리까지 */
const money = (value: number | null | undefined, currency: string): string => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) return EMPTY
	const cur = currency === 'KRW' ? 'KRW' : 'USD'
	return Number(value).toLocaleString('en-US', {
		style: 'currency',
		currency: cur,
		maximumFractionDigits: cur === 'KRW' ? 0 : 2,
	})
}

const datetime = (value: string): string =>
	value !== '' ? dayjs(value).tz().format('YYYY-MM-DD HH:mm') : EMPTY

const clean = (v: string | null | undefined): string =>
	v === null || v === undefined || v === 'undefined' || v === 'null' ? '' : String(v)

const formatPhone = (order: OrderType): string => {
	const raw = clean(order.phoneNumber)
	if (raw === '') return EMPTY
	const cc = clean(order.phoneCountryNumber)
	try {
		if (isValidPhoneNumber(raw, order.countryCode as CountryCode)) {
			const asYouType = new AsYouType()
			const formatted = asYouType.input(`+${cc}${raw}`)
			const info = asYouType.getNumber()
			return info?.country === undefined ? formatted : `${formatted} (${info?.country})`
		}
	} catch {
		/* 형식 판정 실패 — 원문으로 */
	}
	return cc !== '' ? `+${cc} ${raw}` : raw
}

/**
 * 주문 자세히 — 오더 관리 그리드의 "자세히" 열이 연다. 전체 화면 오버레이(FullModalLayout overlay).
 *
 * 한 장에 주문 하나를 다 읽는다: 머리(번호·상태·시각) → 큰 수 네 개 → 주문 항목 표와 합계 →
 * 고객·수취인 | 결제 → 배송(주문 필드 + SHIPMENT 건) → 이력 → 하단 동작 바(주문확인·환불·삭제).
 * 항목(detailList)은 목록 응답에 실려 오고, 배송 건만 열 때 `GET /shipment?orderId=` 로 읽는다.
 * 동작 결과는 전역 토스트로 나간다 — 시트 위에 뜨고 시트가 닫혀도 남는다.
 */
export const OrderDetailModal = () => <OrderDetailSheet />

const OrderDetailSheet = () => {
	const reduxDispatch = useAppDispatch()

	const orderId: string = useAppSelector(state => state.order.detailModalOrderId)
	const acting: boolean = useAppSelector(state => state.order.acting)
	const refundLockReason: string = useAppSelector(state => state.order.refundLockReason)

	const orderList: OrderType[] = useAppSelector(state => state.order.orderList)
	const detailOrder: OrderType | null = useAppSelector(state => state.order.detailOrder)
	const detailOrderStatus: ApiGetDataStatus = useAppSelector(
		state => state.order.detailOrderStatus,
	)
	const shipments: ShipmentType[] = useAppSelector(state => state.shipment.orderShipments)
	const shipmentsStatus: ApiGetDataStatus = useAppSelector(
		state => state.shipment.orderShipmentsStatus,
	)
	/** 되돌릴 수 없는 동작은 문장으로 한 번 더 묻는다 */
	const [confirm, setConfirm] = React.useState<'' | 'refund' | 'cancel' | 'paid'>('')
	/** 입금 확인(도매) — 실제 입금액(기본 = 주문 합계)과 참조 한 줄 */
	const [paidAmountInput, setPaidAmountInput] = React.useState<string>('')
	const [paidRef, setPaidRef] = React.useState<string>('')
	/** 환불 사유 — 1단계에서 적고, 잠금을 넘길 때(2단계)도 같은 문장을 쓴다 */
	const [refundReason, setRefundReason] = React.useState<string>('')
	/** 취소 사유 — 환불 사유와 다른 칸에 남는다(`DELETE_REASON`) */
	const [cancelReason, setCancelReason] = React.useState<string>('')

	/*
		⚠ 값이 없을 수 있다는 전제로 읽는다. 스토어는 sessionStorage 에 persist 되는데, **배포로 상태가
		늘어나면 지난 세션에는 그 키가 없다** — `detailOrder` 가 `undefined` 로 되살아난다.
		`!== null` 로만 막으면 `undefined` 가 통과해 `undefined.id` 에서 화면 전체가 죽는다
		(2026-09-20 운영 사고). 그래서 옵셔널 체이닝으로 읽고 목록도 빈 배열로 받는다.
	*/
	const safeOrderList: OrderType[] = Array.isArray(orderList) ? orderList : []
	const index = React.useMemo(
		() => safeOrderList.findIndex(o => o?.id === orderId),
		[safeOrderList, orderId],
	)
	/*
		목록이 정본이다. 링크로 바로 들어와 목록에 없을 때만(딥링크·필터 밖 주문) 서버에서 읽은
		사본을 쓴다 — 그 사본은 다른 주문으로 옮겨가면 스토어가 버린다.
	*/
	const order: OrderType | undefined =
		index >= 0
			? safeOrderList[index]
			: detailOrder?.id === orderId
			? detailOrder ?? undefined
			: undefined
	const open = orderId !== ''
	const missing = open && order === undefined

	/*
		잠금 확인(§2.2 2단계)은 **이번에 환불을 누른 흐름 안에서만** 산다. 스토어가 sessionStorage 에
		persist 되므로 이걸 지우지 않으면 주문을 다시 열었을 때 묻지도 않은 모달이 먼저 뜬다.
		하단 환불 버튼은 잠금과 무관하게 서 있고, 잠금은 누른 뒤에 서버가 알려 준다.
	*/
	React.useEffect(() => {
		reduxDispatch(orderActions.REFUND_LOCK_REASON(''))
		setRefundReason('')
		setCancelReason('')
		setConfirm('')
	}, [orderId, reduxDispatch])

	// 열리면 그 주문의 배송 건을 읽고, 닫히면 비운다
	React.useEffect(() => {
		reduxDispatch(shipmentActions.TRIGGER_ORDER_SHIPMENTS(orderId))
	}, [orderId, reduxDispatch])

	// 목록에 없는 주문이면 한 번 읽어 본다(새로고침·링크 공유). 없으면 '찾지 못함'을 보여준다
	React.useEffect(() => {
		if (missing && detailOrderStatus === 'init') {
			reduxDispatch(orderActions.FETCH_ORDER_ONE(orderId))
		}
	}, [missing, detailOrderStatus, orderId, reduxDispatch])

	const onClose = React.useCallback(() => {
		reduxDispatch(routeActions.NAVIGATE_PATH(LIST_PATH))
	}, [reduxDispatch])

	const goTo = (nextIndex: number) => {
		const next = safeOrderList[nextIndex]
		if (next !== undefined) {
			reduxDispatch(routeActions.NAVIGATE_PATH(detailPath(next.id)))
		}
	}

	if (!open) return null

	// 아직 못 찾았다 — 읽는 중이거나, 그런 주문번호가 없다
	if (order === undefined) {
		return (
			<FullModalLayout
				overlay
				size="large"
				title={orderId}
				className="orderDetailModal"
				onCloseClick={onClose}
				customTitle={
					<View className="orderHead" css={headStyle}>
						<span className="boKicker">주문</span>
						<View className="orderHeadRow">
							<h1 className="orderNumber">{orderId}</h1>
						</View>
					</View>
				}
			>
				<View css={style}>
					<View className="boSection">
						<Text className="boEmpty">
							{detailOrderStatus === 'loading' || detailOrderStatus === 'init'
								? '주문을 읽는 중…'
								: '이 주문번호를 찾지 못했습니다. 삭제되었거나 번호가 다릅니다.'}
						</Text>
						{detailOrderStatus === 'empty' && (
							<button type="button" className="boBtn" onClick={onClose}>
								목록으로
							</button>
						)}
					</View>
				</View>
			</FullModalLayout>
		)
	}

	const currency = order.currency === 'KRW' ? 'KRW' : 'USD'
	const lines: OrderDetailType[] = order.detailList.filter(d => d.deletedAt === '')
	const quantity = lines.reduce((acc, d) => acc + Number(d.quantity), 0)
	const lineAmount = (d: OrderDetailType) =>
		(Number(d.unitAmount) + Number(d.optionAmount)) * Number(d.quantity)

	const receiverName = [clean(order.receiverGivenName), clean(order.receiverFamilyName)]
		.filter(s => s !== '')
		.join(' ')
	const addressLine1 = [clean(order.address1), clean(order.address2)]
		.filter(s => s !== '')
		.join(', ')
	const addressLine2 = [
		clean(order.city),
		clean(order.state),
		clean(order.zip),
		clean(order.country) +
			(clean(order.countryCode) !== '' ? ` (${order.countryCode})` : ''),
	]
		.filter(s => s !== '')
		.join(', ')

	const paymentMethod =
		PAYMENT_METHOD_TITLE[order.paymentMethod] ??
		(clean(order.paymentMethod) !== '' ? order.paymentMethod : EMPTY)

	const activeShipment = shipments.find(s => s.status !== 'canceled')
	const shippingSummary =
		order.status === 'completed' || activeShipment?.status === 'delivered'
			? '배송 완료'
			: order.trackingNumber !== ''
			? '배송중'
			: order.status === 'preparing'
			? '준비중'
			: EMPTY

	/**
	 * 한 일을 한 사람의 이름. 서버가 `USERS` 를 조인해 준다(ORDER_PLAN §1.2).
	 * 고객이 만든 주문은 `USERS` 에 없어 이름이 비는데, 그때는 저장된 ID 를 그대로 보여 준다.
	 */
	const actorName = (id: string, name: string): string =>
		clean(name) === '' ? clean(id) : clean(name)

	// 이력 — 값이 있는 것만, 시간순. `note` 는 그 줄에 딸린 한 줄(환불 사유 등)
	const history: {
		at: string
		title: string
		by: string
		byEmail: string
		note: string
	}[] = [
		{ at: order.createdAt, title: '주문 생성', by: '', byEmail: '', note: '' },
		{ at: order.preparedAt, title: '주문확인', by: '', byEmail: '', note: '' },
		{ at: order.deliveryCompletedAt, title: '배송 완료', by: '', byEmail: '', note: '' },
		{
			at: order.refundedAt,
			title: '환불',
			by: actorName(order.refundedBy, order.refundedByName),
			byEmail: clean(order.refundedByEmail),
			note:
				clean(order.refundReason) === ''
					? ''
					: order.refundOverrideYn === 1
					? `${order.refundReason} · 환불 잠금을 넘김`
					: order.refundReason,
		},
		{
			at: order.deletedAt,
			title: '취소',
			by: actorName(order.deletedBy, order.deletedByName),
			byEmail: clean(order.deletedByEmail),
			note: clean(order.deleteReason),
		},
		{
			at: order.updatedAt,
			title: '마지막 수정',
			by: actorName(order.updatedBy, order.updatedByName),
			byEmail: clean(order.updatedByEmail),
			note: '',
		},
	]
		.filter(h => clean(h.at) !== '')
		.sort((a, b) => dayjs(a.at).valueOf() - dayjs(b.at).valueOf())

	// 동작 — 주문확인은 신규 · 입금 대기(도매는 입금 전에도 출고한다, 2026-09-27)
	const isPaypal = order.paymentMethod.startsWith('paypal')
	const isCard = order.paymentMethod === 'card_usd' || order.paymentMethod === 'card_krw'
	const canPrepare = order.status === 'new' || order.status === 'awaitingPayment'
	// 입금 확인 — 도매 · 미입금이면 배송 상태와 무관하게. 되돌리는 경로는 없다 (DISPATCH_PLAN §5.3)
	const canMarkPaid =
		order.channel === 'wholesale' && order.paidYn === 0 && order.status !== 'deleted'

	/*
		환불을 **화면이** 막는 것은 둘뿐이다 (사용자 결정 2026-09-20).
		  · 이미 환불된 주문 — 누르면 돈이 두 번 나간다. 서버도 409 로 막는다
		  · 결제 금액 없음   — 돌려줄 돈이 없다
		나머지(결제수단이 비었거나 캡처 ID·tid 가 목록에 안 실려 온 경우)는 **막지 않는다.**
		서버가 `PAYPAL_CAPTURE` · `PAYVERSE_{KRW,USD}_APPROVAL` 에서 참조를 다시 찾고, 그래도 없으면
		`unsupported` 로 이유를 돌려준다(ORDER_PLAN §2.2). 옛 주문은 목록 값이 비어 있어도 서버는 찾아낸다.
	*/
	// 취소 — 이미 취소된 주문만 막는다. 돈과 무관하므로 환불 여부는 보지 않는다
	const canCancel = order.status !== 'deleted'

	const refundBlock =
		order.channel === 'wholesale' && order.paidYn === 0
			? '입금 전 — 받은 돈이 없습니다'
			: order.status === 'refunded'
			? '이미 환불된 주문'
			: Number(order.paidAmount) <= 0
			? '결제 금액 없음'
			: ''
	const canRefund = refundBlock === ''

	// 막지는 않고 미리 알려만 준다 — 서버가 참조를 못 찾으면 실패로 돌아온다
	const refundWarn =
		refundBlock !== ''
			? ''
			: isPaypal && order.paypalCaptureId === ''
			? 'PayPal 캡처 ID 가 목록에 없습니다 — 서버가 다시 찾습니다'
			: isCard && order.gatewayRef === ''
			? 'PayVerse 거래번호가 목록에 없습니다 — 서버가 다시 찾습니다'
			: !isPaypal && !isCard
			? `결제수단이 ${
					order.paymentMethod || '비어 있습니다'
			  } — 서버가 결제 기록에서 찾습니다`
			: ''
	const gatewayName = isPaypal ? 'PayPal' : isCard ? 'PayVerse' : '결제 게이트웨이'

	return (
		<FullModalLayout
			overlay
			size="large"
			title={order.id}
			className="orderDetailModal"
			onCloseClick={onClose}
			leftSideContent={
				// 목록 안에서만 이전·다음. 링크로 바로 들어온 주문은 앞뒤가 없다
				index < 0 ? undefined : (
					<View className="orderNav" css={navStyle}>
						<Pressable
							className="orderNavBtn"
							disabled={index <= 0}
							onClick={() => goTo(index - 1)}
						>
							<Icon name="chevron-left" className="orderNavIcon" />
							<span>이전</span>
						</Pressable>
						<span className="orderNavPos">
							{index + 1} / {safeOrderList.length}
						</span>
						<Pressable
							className="orderNavBtn"
							disabled={index >= safeOrderList.length - 1}
							onClick={() => goTo(index + 1)}
						>
							<span>다음</span>
							<Icon name="chevron-right" className="orderNavIcon" />
						</Pressable>
					</View>
				)
			}
			customTitle={
				<View className="orderHead" css={headStyle}>
					<span className="boKicker">주문</span>
					<View className="orderHeadRow">
						<h1 className="orderNumber">{order.id}</h1>
						<span className={boStatusChipClass(order.status)}>
							{ORDER_STATUS_TITLE[order.status] ?? order.status}
						</span>
						{fulfillmentChipTitle(order) !== '' && (
							<span className="boChip outline">{fulfillmentChipTitle(order)}</span>
						)}
						{order.channel === 'wholesale' && (
							<span className="boChip outline">도매</span>
						)}
						{order.channel === 'wholesale' &&
							order.paidYn === 0 &&
							order.status !== 'awaitingPayment' && (
								<span className={boStatusChipClass('awaitingPayment')}>미입금</span>
							)}
					</View>
					<span className="orderHeadMeta">
						{[
							datetime(order.createdAt),
							// 결제수단이 비어 있는 옛 주문에서 빈 대시를 세우지 않는다
							clean(order.paymentMethod) !== '' ? paymentMethod : '',
							currency,
							order.invoiceNumber !== '' ? `인보이스 ${order.invoiceNumber}` : '',
							clean(order.seasonId) !== '' ? `시즌 ${order.seasonId}` : '',
						]
							.filter(aPart => aPart !== '')
							.join(' · ')}
					</span>
				</View>
			}
		>
			<View css={style}>
				<BoDetailSections>
					<BoDetailSection id="summary" label="요약">
						{/* 큰 수 */}
						<View className="statRow">
							<View className="boStat">
								<span className="boKicker">결제 금액</span>
								<span className="boStatValue">{money(order.paidAmount, currency)}</span>
							</View>
							<View className="boStat">
								<span className="boKicker">수량</span>
								<span className="boStatValue">{quantity}</span>
							</View>
							<View className="boStat">
								<span className="boKicker">배송</span>
								<span className="boStatValue statText">{shippingSummary}</span>
							</View>
							<View className="boStat">
								<span className="boKicker">환불</span>
								<span
									className={`boStatValue ${order.refundAmount > 0 ? '' : 'statFaint'}`}
								>
									{order.refundAmount > 0 ? money(order.refundAmount, currency) : EMPTY}
								</span>
							</View>
						</View>
					</BoDetailSection>
					<BoDetailSection id="items" label="항목">
						{/* 주문 항목 */}
						<View className="boSection">
							<h2 className="boSectionTitle">주문 항목</h2>
							{lines.length === 0 ? (
								<Text className="boEmpty">항목이 없습니다.</Text>
							) : (
								<View className="tableWrap">
									<table className="lineTable">
										<thead>
											<tr>
												<th className="colIdx">#</th>
												<th>상품</th>
												<th>옵션</th>
												<th className="num">단가</th>
												<th className="num colQty">수량</th>
												<th className="num">금액</th>
											</tr>
										</thead>
										<tbody>
											{lines.map((d, i) => (
												<tr key={d.id !== '' ? d.id : `${d.productId}-${i}`}>
													<td className="colIdx">{i + 1}</td>
													<td className="cardTitle">
														<span className="lineName">
															{d.productName || d.productId}
														</span>
														{clean(d.pricePhase) !== '' && (
															<span className="lineSub">{d.pricePhase}</span>
														)}
													</td>
													<td data-label="옵션">
														{clean(d.optionName) !== '' ? (
															<>
																<span>{d.optionName}</span>
																{Number(d.optionAmount) !== 0 && (
																	<span className="lineSub">
																		+{money(d.optionAmount, currency)}
																	</span>
																)}
															</>
														) : (
															<span className="lineFaint">{EMPTY}</span>
														)}
													</td>
													<td className="num" data-label="단가">
														{money(d.unitAmount, currency)}
													</td>
													<td className="num colQty" data-label="수량">
														{d.quantity}
													</td>
													<td className="num" data-label="금액">
														{money(lineAmount(d), currency)}
													</td>
												</tr>
											))}
										</tbody>
										<tfoot>
											<tr>
												<td colSpan={5} className="totLabel">
													상품 합계
												</td>
												<td className="num">{money(order.totalAmount, currency)}</td>
											</tr>
											{Number(order.discountAmount) !== 0 && (
												<tr>
													<td colSpan={5} className="totLabel">
														할인
														{clean(order.couponCode) !== '' && (
															<span className="lineSub inline">
																쿠폰 {order.couponCode}
															</span>
														)}
													</td>
													<td className="num">
														−{money(order.discountAmount, currency)}
													</td>
												</tr>
											)}
											<tr>
												<td colSpan={5} className="totLabel">
													배송비
												</td>
												<td className="num">{money(order.shippingAmount, currency)}</td>
											</tr>
											{Number(order.vatUsd) !== 0 && (
												<tr>
													<td colSpan={5} className="totLabel">
														부가세
													</td>
													<td className="num">{money(order.vatUsd, 'USD')}</td>
												</tr>
											)}
											<tr className="totPaid">
												<td colSpan={5} className="totLabel">
													결제 금액
												</td>
												<td className="num">{money(order.paidAmount, currency)}</td>
											</tr>
											{order.refundAmount > 0 && (
												<tr>
													<td colSpan={5} className="totLabel">
														환불
														{clean(order.refundRef) !== '' && (
															<span className="lineSub inline">{order.refundRef}</span>
														)}
													</td>
													<td className="num">−{money(order.refundAmount, currency)}</td>
												</tr>
											)}
										</tfoot>
									</table>
								</View>
							)}
						</View>
					</BoDetailSection>
					<BoDetailSection id="customer" label="고객 · 결제">
						{/* 고객 · 수취인 | 결제 */}
						<View className="twoCol">
							<View className="boSection">
								<h2 className="boSectionTitle">고객 · 수취인</h2>
								<View className="boSpecRow">
									<span className="boSpecLabel">고객</span>
									<span className="boSpecValue">
										{clean(order.customerEmail) || EMPTY}
										{clean(order.customerId) !== '' && (
											<span className="lineSub inline mono">{order.customerId}</span>
										)}
									</span>
								</View>
								<View className="boSpecRow">
									<span className="boSpecLabel">수취인</span>
									<span className="boSpecValue">{receiverName || EMPTY}</span>
								</View>
								<View className="boSpecRow">
									<span className="boSpecLabel">전화</span>
									<span className="boSpecValue">{formatPhone(order)}</span>
								</View>
								<View className="boSpecRow top">
									<span className="boSpecLabel">주소</span>
									<span className="boSpecValue">
										{addressLine1 === '' && addressLine2 === '' ? (
											EMPTY
										) : (
											<>
												{addressLine1 !== '' && (
													<span className="block">{addressLine1}</span>
												)}
												{addressLine2 !== '' && (
													<span className="block">{addressLine2}</span>
												)}
											</>
										)}
									</span>
								</View>
							</View>

							<View className="boSection">
								<h2 className="boSectionTitle">결제</h2>
								<View className="boSpecRow">
									<span className="boSpecLabel">결제수단</span>
									<span className="boSpecValue">{paymentMethod}</span>
								</View>
								<View className="boSpecRow">
									<span className="boSpecLabel">청구</span>
									<span className="boSpecValue">
										{money(order.paidAmount, currency)}
										{currency === 'KRW' && Number(order.paidUsd) > 0 && (
											<span className="lineSub inline">
												USD {money(order.paidUsd, 'USD')}
											</span>
										)}
									</span>
								</View>
								<View className="boSpecRow top">
									<span className="boSpecLabel">USD 기준</span>
									<span className="boSpecValue usdGrid">
										<span>물건가격 {money(order.totalUsd, 'USD')}</span>
										<span>부가세 {money(order.vatUsd, 'USD')}</span>
										<span>운송비 {money(order.shippingUsd, 'USD')}</span>
										<span>할인가격 {money(order.discountedUsd, 'USD')}</span>
										<span>결제금액 {money(order.paidUsd, 'USD')}</span>
									</span>
								</View>
								<View className="boSpecRow">
									<span className="boSpecLabel">거래번호</span>
									<span className="boSpecValue">
										{clean(order.gatewayRef) !== '' ? (
											<span className="mono">{order.gatewayRef}</span>
										) : (
											EMPTY
										)}
										{clean(order.paypalCaptureId) !== '' && (
											<span className="lineSub inline mono">
												캡처 {order.paypalCaptureId}
											</span>
										)}
									</span>
								</View>
								<View className="boSpecRow">
									<span className="boSpecLabel">인보이스</span>
									<span className="boSpecValue">
										{clean(order.invoiceUrl) !== '' ? (
											<a
												className="boLink"
												href={order.invoiceUrl}
												target="_blank"
												rel="noreferrer"
											>
												{clean(order.invoiceNumber) || '인보이스 열기'}
											</a>
										) : (
											clean(order.invoiceNumber) || EMPTY
										)}
									</span>
								</View>
								<View className="boSpecRow">
									<span className="boSpecLabel">쿠폰</span>
									<span className="boSpecValue">
										{clean(order.couponCode) !== '' || clean(order.couponId) !== '' ? (
											<>
												{clean(order.couponCode) || EMPTY}
												{Number(order.couponDiscountUsd) !== 0 && (
													<span className="lineSub inline">
														−{money(order.couponDiscountUsd, 'USD')}
													</span>
												)}
												{clean(order.couponId) !== '' && (
													<span className="lineSub inline mono">{order.couponId}</span>
												)}
											</>
										) : (
											EMPTY
										)}
									</span>
								</View>
								{clean(order.holdExpiresAt) !== '' && (
									<View className="boSpecRow">
										<span className="boSpecLabel">홀드 만료</span>
										<span className="boSpecValue">{datetime(order.holdExpiresAt)}</span>
									</View>
								)}
								{order.refundAmount > 0 && (
									<View className="boSpecRow">
										<span className="boSpecLabel">환불</span>
										<span className="boSpecValue">
											{money(order.refundAmount, currency)}
											{clean(order.refundRef) !== '' && (
												<span className="lineSub inline mono">{order.refundRef}</span>
											)}
										</span>
									</View>
								)}
							</View>
						</View>
					</BoDetailSection>
					<BoDetailSection id="shipping" label="배송">
						{/* 배송 */}
						<View className="boSection">
							<h2 className="boSectionTitle">배송</h2>
							<View className="twoCol tight">
								<View>
									<View className="boSpecRow">
										<span className="boSpecLabel">운송회사</span>
										<span className="boSpecValue">
											{clean(order.deliveryCompany) || EMPTY}
										</span>
									</View>
									<View className="boSpecRow">
										<span className="boSpecLabel">송장번호</span>
										<span className="boSpecValue">
											{clean(order.trackingNumber) !== '' ? (
												<a
													className="boLink"
													href={`https://www.fedex.com/fedextrack/?trknbr=${order.trackingNumber}`}
													target="_blank"
													rel="noreferrer"
												>
													{order.trackingNumber}
												</a>
											) : (
												EMPTY
											)}
										</span>
									</View>
								</View>
								<View>
									<View className="boSpecRow">
										<span className="boSpecLabel">운송 Rate</span>
										<span className="boSpecValue">
											{clean(order.shippingRateType) || EMPTY}
											{clean(order.boxType) !== '' && (
												<span className="lineSub inline">박스 {order.boxType}</span>
											)}
										</span>
									</View>
									<View className="boSpecRow">
										<span className="boSpecLabel">배송 완료</span>
										<span className="boSpecValue">
											{datetime(order.deliveryCompletedAt)}
										</span>
									</View>
								</View>
							</View>

							{shipmentsStatus === 'loading' ? (
								<Text className="boEmpty">배송 건을 읽는 중…</Text>
							) : shipments.length === 0 ? (
								<Text className="boEmpty">이 주문에 발급된 라벨이 없습니다.</Text>
							) : (
								<View className="shipmentList">
									{shipments.map(s => (
										<View className="shipmentRow" key={s.id}>
											<span
												className={boStatusChipClass(SHIPMENT_STATUS_KEY[s.status] ?? '')}
											>
												{SHIPMENT_STATUS_TITLE[s.status] ?? s.status}
											</span>
											<View className="shipmentMain">
												<span className="shipmentTitle">
													{s.carrier} · {s.serviceType}
													{s.trackingNumber !== '' && (
														<>
															{' · '}
															<a
																className="boLink"
																href={`https://www.fedex.com/fedextrack/?trknbr=${s.trackingNumber}`}
																target="_blank"
																rel="noreferrer"
															>
																{s.trackingNumber}
															</a>
														</>
													)}
												</span>
												<span className="shipmentSub">
													{s.boxCode !== '' && `${s.boxCode} · `}
													{s.widthCm}×{s.heightCm}×{s.depthCm} cm · {s.weightKg} kg
													{s.hasLabel === 1 && ' · 라벨 저장됨'}
												</span>
											</View>
											<View className="shipmentTrack">
												<span className="shipmentStatus">
													{s.lastStatusDesc || EMPTY}
												</span>
												<span className="shipmentSub">
													{s.lastTrackedAt !== '' && `${datetime(s.lastTrackedAt)} · `}
													스캔 {s.scanEvents.length}
												</span>
											</View>
										</View>
									))}
								</View>
							)}
						</View>
					</BoDetailSection>
					<BoDetailSection id="history" label="이력">
						{/* 이력 */}
						<View className="boSection">
							<h2 className="boSectionTitle">이력</h2>
							{history.length === 0 ? (
								<Text className="boEmpty">기록이 없습니다.</Text>
							) : (
								<View className="historyList">
									<View className="historyRow historyHead">
										<span>날짜</span>
										<span>내용</span>
										<span>수정자 이름</span>
										<span>수정자 이메일</span>
									</View>
									{history.map((h, i) => (
										<View className="historyRow" key={`${h.title}-${i}`}>
											<span className="historyAt">{datetime(h.at)}</span>
											<View className="historyBody">
												<span className="historyTitle">{h.title}</span>
												{h.note !== '' && <span className="historyNote">{h.note}</span>}
											</View>
											<span className="historyBy">{h.by || EMPTY}</span>
											<span className="historyBy">{h.byEmail || EMPTY}</span>
										</View>
									))}
								</View>
							)}
						</View>
					</BoDetailSection>
				</BoDetailSections>
				{/*
					동작 — 블랙은 주문확인 하나, 되돌릴 수 없는 환불·취소는 고스트 + 확인 문장 + 사유.
					환불은 돈, 취소는 흐름. 두 축은 따로 논다 (§2.4).
				*/}
				<View className="actionBar">
					<View className="actionBarLeft">
						{canMarkPaid && (
							<>
								<button
									type="button"
									className="boBtn"
									disabled={acting}
									onClick={() => {
										setPaidAmountInput(String(order.paidAmount))
										setPaidRef('')
										setConfirm('paid')
									}}
								>
									{acting ? '처리 중…' : '입금 확인'}
								</button>
								<span className="actionHint">
									도매 주문 — 입금 전에도 주문확인·배송을 진행할 수 있습니다. 입금은
									여기서나 정산에서 확인합니다
								</span>
							</>
						)}
						{
							<>
								<button
									type="button"
									className="boBtn"
									disabled={!PREPARE_ENABLED || !canPrepare || acting}
									onClick={() =>
										PREPARE_ENABLED &&
										reduxDispatch(orderActions.TRIGGER_PREPARE_ORDER(order.id))
									}
								>
									{acting ? '처리 중…' : '주문확인 → 배송 준비중'}
								</button>
								<span className="actionHint">
									{!PREPARE_ENABLED
										? '기능 연결 전 — 버튼 자리만 잡았습니다'
										: canPrepare
										? 'ORDER.PREPARED_AT 에 지금 시각이 찍힙니다'
										: '신규 주문 · 입금 대기에서만 누를 수 있습니다'}
								</span>
							</>
						}
					</View>
					<View className="actionBarRight">
						{REFUND_ENABLED && !canRefund && (
							<span className="actionHint">환불 불가 — {refundBlock}</span>
						)}
						{REFUND_ENABLED && canRefund && refundWarn !== '' && (
							<span className="actionHint">{refundWarn}</span>
						)}
						<button
							type="button"
							className="boBtnGhost"
							disabled={!REFUND_ENABLED || !canRefund || acting}
							onClick={() => {
								if (!REFUND_ENABLED) return
								setRefundReason('')
								reduxDispatch(orderActions.REFUND_LOCK_REASON(''))
								setConfirm('refund')
							}}
						>
							{acting ? '처리 중…' : '환불'}
						</button>
						<button
							type="button"
							className="boBtnGhost"
							disabled={!CANCEL_ENABLED || !canCancel || acting}
							onClick={() => {
								if (!CANCEL_ENABLED) return
								setCancelReason('')
								setConfirm('cancel')
							}}
						>
							주문 취소
						</button>
					</View>
				</View>
			</View>

			{/* 입금 확인(도매) — 실제 입금액과 참조. PAID_YN · PAID_AT · bank_transfer 가 찍힌다 (DISPATCH_PLAN §5.3) */}
			<Modal
				show={confirm === 'paid'}
				target="orderMarkPaidConfirm"
				title="입금 확인"
				closeTitle="취소"
				submitTitle="입금 확인"
				submitIcon="cash-check"
				isSubmitDisabled={
					paidAmountInput.trim() !== '' &&
					Number.isNaN(Number(paidAmountInput.replace(/,/g, '')))
				}
				onCloseModal={() => setConfirm('')}
				onSubmit={() =>
					reduxDispatch(
						orderActions.TRIGGER_MARK_PAID({
							orderId: order.id,
							amount:
								paidAmountInput.trim() === ''
									? null
									: Number(paidAmountInput.replace(/,/g, '')),
							ref: paidRef.trim(),
						}),
					)
				}
			>
				<Text>
					{`주문 합계 ${money(
						order.paidAmount,
						currency,
					)}. 입금액이 다르면 고쳐 적습니다 — 주문의 결제 금액이 그 값으로 바뀝니다. 되돌릴 수 없습니다.`}
				</Text>
				<input
					className="reasonField"
					css={reasonStyle}
					type="text"
					inputMode="decimal"
					value={paidAmountInput}
					placeholder={`입금액 (${currency})`}
					onChange={e => setPaidAmountInput(e.target.value)}
				/>
				<input
					className="reasonField"
					css={reasonStyle}
					type="text"
					maxLength={100}
					value={paidRef}
					placeholder="참조 — 입금자 · 은행 · 전표 (선택)"
					onChange={e => setPaidRef(e.target.value)}
				/>
			</Modal>

			{/* 환불 1단계 — 사유를 적는다. 사유는 주문에 남고 이력에 보인다 */}
			<Modal
				show={confirm === 'refund'}
				target="orderRefundConfirm"
				title="환불"
				closeTitle="취소"
				submitTitle="환불"
				submitIcon="cash-refund"
				isSubmitDisabled={refundReason.trim() === ''}
				onCloseModal={() => setConfirm('')}
				onSubmit={() =>
					reduxDispatch(
						orderActions.TRIGGER_REFUND_ONE({
							orderId: order.id,
							reason: refundReason.trim(),
						}),
					)
				}
			>
				<Text>
					{`${money(
						order.paidAmount,
						currency,
					)} 를 ${gatewayName} 로 환불합니다. 되돌릴 수 없고, 재고가 돌아옵니다.`}
				</Text>
				{refundWarn !== '' && <Text>{refundWarn}</Text>}
				<Text>
					{`${gatewayName} 에 이미 환불돼 있으면 돈은 두 번 나가지 않고 주문만 정리됩니다.`}
				</Text>
				<textarea
					className="reasonField"
					css={reasonStyle}
					value={refundReason}
					rows={3}
					maxLength={500}
					placeholder="환불 사유 (필수) — 주문 이력에 남습니다"
					onChange={e => setRefundReason(e.target.value)}
				/>
			</Modal>

			{/*
				환불 2단계 — 이미 출고돼 서버가 막은 주문. 관리자는 넘어갈 수 있고(사용자 결정 2026-09-20),
				넘어가면 `[REFUND]:LOCK-OVERRIDE` 로그와 `REFUND_OVERRIDE_YN = 1` 이 남는다.
			*/}
			<Modal
				show={refundLockReason !== ''}
				target="orderRefundOverrideConfirm"
				title="이미 출고된 주문입니다"
				closeTitle="그만두기"
				submitTitle="그래도 환불"
				submitIcon="cash-refund"
				isSubmitDisabled={refundReason.trim() === ''}
				onCloseModal={() => reduxDispatch(orderActions.REFUND_LOCK_REASON(''))}
				onSubmit={() =>
					reduxDispatch(
						orderActions.TRIGGER_REFUND_ONE({
							orderId: order.id,
							reason: refundReason.trim(),
							overrideLock: true,
						}),
					)
				}
			>
				<Text>{refundLockReason}</Text>
				<Text>
					{`고객 정책은 출고 전까지만 환불합니다. 그래도 환불하면 넘어간 기록(${'REFUND_OVERRIDE_YN'})과 사유가 주문에 남고 서버 로그에도 남습니다.`}
				</Text>
				<Text>{`사유: ${refundReason.trim()}`}</Text>
			</Modal>

			{/*
				주문 취소 — 이 주문을 더 진행하지 않는다는 표시. 저장은 소프트 삭제이고 **돈은 움직이지 않는다.**
				환불이 필요하면 환불을 따로 누른다 (§2.4).
			*/}
			<Modal
				show={confirm === 'cancel'}
				target="orderCancelConfirm"
				title="주문 취소"
				closeTitle="취소"
				submitTitle="주문 취소"
				submitIcon="close-circle-outline"
				isSubmitDisabled={cancelReason.trim() === ''}
				onCloseModal={() => setConfirm('')}
				onSubmit={() =>
					reduxDispatch(
						orderActions.DELETE_ORDER({
							orderId: order.id,
							reason: cancelReason.trim(),
						}),
					)
				}
			>
				<Text>
					{`주문 ${order.id} 를 취소합니다. 배송·완료 흐름에서 빠지고 목록에는 회색 '취소' 로 남습니다.`}
				</Text>
				<Text>
					{order.refundedAt === '' && Number(order.paidAmount) > 0
						? '돈은 움직이지 않습니다 — 환불이 필요하면 환불을 따로 누릅니다.'
						: '돈은 움직이지 않습니다.'}
				</Text>
				<textarea
					className="reasonField"
					css={reasonStyle}
					value={cancelReason}
					rows={3}
					maxLength={500}
					placeholder="취소 사유 (필수) — 주문 이력에 남습니다"
					onChange={e => setCancelReason(e.target.value)}
				/>
			</Modal>
		</FullModalLayout>
	)
}

/** 확인 모달 안의 환불 사유 입력 */
const reasonStyle = css({
	width: '100%',
	marginTop: 12,
	padding: '8px 10px',
	border: `1px solid ${mono.line}`,
	borderRadius: 2,
	fontFamily: boFont.ui,
	fontSize: 13,
	lineHeight: 1.5,
	color: mono.ink,
	resize: 'vertical',
	'&:focus': { outline: 'none', borderColor: mono.ink },
	'&::placeholder': { color: mono.faint },
})

/** 상단 바 좌측 — 목록 안에서 이전·다음 */
const navStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: 4,
	fontFamily: boFont.ui,

	'.orderNavBtn': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 2,
		height: 32,
		padding: '0 8px',
		borderRadius: 2,
		fontSize: 13,
		fontWeight: 500,
		color: mono.dim,
		transition: 'color 200ms ease-out',
		'&:hover:not(:disabled)': { color: mono.ink },
		'&:disabled': { opacity: 0.3, cursor: 'default' },
		'.orderNavIcon': { fontSize: 18, color: 'inherit' },
	},
	'.orderNavPos': {
		padding: '0 6px',
		fontSize: 12,
		color: mono.faint,
		fontVariantNumeric: 'tabular-nums',
	},
})

/** 머리 — 키커 · 주문번호(Thin) + 상태 칩 · 메타 한 줄 */
const headStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	fontFamily: boFont.ui,

	'.boKicker': {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	'.orderHeadRow': { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
	'.orderNumber': {
		margin: 0,
		fontSize: 'clamp(28px, 3.2vw, 40px)',
		fontWeight: 200,
		lineHeight: 1.1,
		letterSpacing: '-0.01em',
		color: mono.ink,
		fontVariantNumeric: 'tabular-nums',
	},
	'.boChip': boShared['.boChip'],
	'.orderHeadMeta': { fontSize: 13, fontWeight: 300, color: mono.dim },
})

const style = css({
	...boShared,
	paddingTop: 8,

	'.statRow': {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: 24,
		padding: '8px 0 28px',
		borderBottom: `1px solid ${mono.line}`,
		'.boStatValue': { fontVariantNumeric: 'tabular-nums' },
		'.statText': { fontSize: 26, lineHeight: '34px' },
		'.statFaint': { color: mono.faint },
	},

	// boShared 조각을 덮어쓰지 않고 잇는다 — 같은 키를 다시 쓰면 객체 리터럴이 통째로 바뀐다
	'.boSection': {
		...boShared['.boSection'],
		'.boSectionTitle': { marginBottom: 4 },
		'&.last': { borderBottom: 0 },
	},

	'.twoCol': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		columnGap: 48,
		'&.tight': { marginBottom: 8 },
	},

	'.boSpecRow': {
		...boShared['.boSpecRow'],
		gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
		'&.top': { alignItems: 'flex-start' },
		'&:last-of-type': { borderBottom: 0 },
	},
	'.boSpecValue': {
		...boShared['.boSpecValue'],
		minWidth: 0,
		overflowWrap: 'anywhere',
		'.block': { display: 'block' },
	},

	// USD 환산 — 그리드의 달러 열(물건가격·부가세·운송비·할인가격·결제금액)을 한 칸에 모은다
	'.usdGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: '2px 16px',
		fontSize: 12,
		color: mono.dim,
		fontVariantNumeric: 'tabular-nums',
	},
	'.mono': { fontFamily: boFont.mono },

	'.lineSub': {
		display: 'block',
		fontSize: 11,
		color: mono.faint,
		marginTop: 2,
		'&.inline': { display: 'inline', marginLeft: 8, marginTop: 0 },
		'&.mono': { fontFamily: boFont.mono },
	},
	'.lineFaint': { color: mono.faint },

	'.tableWrap': { width: '100%', overflowX: 'auto', paddingTop: 8 },
	'.lineTable': {
		width: '100%',
		borderCollapse: 'collapse',
		fontSize: 13,
		fontWeight: 300,
		color: mono.ink,
		'th, td': {
			padding: '10px 8px',
			textAlign: 'left',
			verticalAlign: 'top',
			borderBottom: `1px solid ${mono.line}`,
		},
		th: {
			fontSize: 11,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
			whiteSpace: 'nowrap',
		},
		'.num': {
			textAlign: 'right',
			fontVariantNumeric: 'tabular-nums',
			whiteSpace: 'nowrap',
		},
		'.colIdx': { width: 32, color: mono.faint, textAlign: 'center' },
		'.colQty': { width: 56 },
		'.lineName': { display: 'block', fontWeight: 400 },
		tfoot: {
			td: { borderBottom: 0, padding: '8px 8px' },
			'tr:first-of-type td': { paddingTop: 14 },
			'.totLabel': { textAlign: 'right', color: mono.dim },
			'.totPaid td': {
				fontWeight: 500,
				fontSize: 14,
				color: mono.ink,
				borderTop: `1px solid ${mono.ink}`,
				paddingTop: 12,
			},
		},
	},

	'.shipmentList': { marginTop: 4 },
	'.shipmentRow': {
		display: 'grid',
		gridTemplateColumns: 'auto minmax(0, 1fr) minmax(0, 320px)',
		alignItems: 'center',
		gap: 16,
		padding: '12px 0',
		borderBottom: `1px solid ${mono.line}`,
		'&:last-of-type': { borderBottom: 0 },
		'.shipmentMain, .shipmentTrack': {
			display: 'flex',
			flexDirection: 'column',
			gap: 3,
			minWidth: 0,
		},
		'.shipmentTitle': { fontSize: 13, fontWeight: 400, color: mono.ink },
		'.shipmentStatus': { fontSize: 13, fontWeight: 300, color: mono.ink },
		'.shipmentSub': { fontSize: 11, color: mono.faint },
	},

	'.actionBar': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		padding: '20px 0 0',
		flexWrap: 'wrap',
		'.actionBarLeft, .actionBarRight': {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			flexWrap: 'wrap',
		},
		'.actionHint': { fontSize: 12, color: mono.faint },
	},

	'.historyList': { marginTop: 4 },
	'.historyRow': {
		display: 'grid',
		gridTemplateColumns: '150px minmax(0, 1fr) 120px 200px',
		gap: 16,
		alignItems: 'center',
		padding: '10px 0',
		borderBottom: `1px solid ${mono.line}`,
		fontSize: 13,
		'&:last-of-type': { borderBottom: 0 },
		'.historyAt': {
			fontFamily: boFont.mono,
			fontSize: 12,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
		'.historyBody': { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
		'.historyTitle': { fontWeight: 400, color: mono.ink },
		// 환불 사유 — 줄 바꿈을 허용한다. 관리자가 문장으로 적는다
		'.historyNote': { fontSize: 12, color: mono.dim, whiteSpace: 'pre-wrap' },
		'.historyBy': {
			fontSize: 12,
			color: mono.faint,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
	},
	// 머리글 — 표의 열 이름. 값 줄과 같은 격자를 쓴다
	'.historyHead': {
		padding: '0 0 8px',
		fontSize: 11,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: mono.faint,
		borderBottom: `1px solid ${mono.line}`,
	},

	// 폰 (RESPONSIVE_PLAN §6.1) — 항목 표는 카드, 동작 바는 하단 sticky.
	// 2026-09-29: 가로 스크롤 표를 걷어냈다(스크롤 안의 스크롤 · 오른쪽 잘림).
	[boMedia.phone]: {
		'.tableWrap': { overflowX: 'visible', paddingTop: 4 },
		'.lineTable': boTableAsCards,
		'.actionBar': {
			position: 'sticky',
			bottom: 0,
			zIndex: 2,
			background: mono.bg,
			paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
			flexWrap: 'wrap',
			'.actionBarLeft, .actionBarRight': { flexWrap: 'wrap' },
			'& button': { minHeight: 44 },
		},
	},

	'@media (max-width: 900px)': {
		'.statRow': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
		'.twoCol': { gridTemplateColumns: 'minmax(0, 1fr)' },
		'.shipmentRow': {
			gridTemplateColumns: 'auto minmax(0, 1fr)',
			'.shipmentTrack': { gridColumn: '1 / -1' },
		},
		'.historyRow': {
			gridTemplateColumns: '130px minmax(0, 1fr)',
			'.historyBy': { gridColumn: 2 },
		},
	},
})
